import _ from 'lodash';
import { type ComponentProps, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventCapture } from '@/core/EventCapture.atom';
import { useAllTrendStoreItems } from '@/core/hooks/useAllTrendStoreItems.hook';
import { DeleteDisplayModal } from '@/displays/DeleteDisplayModal.molecule';
import { MoveDisplayModal } from '@/displays/MoveDisplayModal.molecule';
import { OverwriteDisplayModal } from '@/displays/OverwriteDisplayModal.molecule';
import { RenameDisplayModal } from '@/displays/RenameDisplayModal.molecule';
import { DISPLAY_DROPDOWN_ACTION_CUSTOM_ID } from '@/displays/displays.constants';
import { canOverwriteScaledDisplay, getDisplayCount } from '@/displays/displays.utilities';
import { useSearchDisplayLoadingId } from '@/search/search.store';
import { errorToast } from '@/utilities/toast.utilities';
import { sqDisplaysApi } from '@seeqdev/api/endpoint/DisplaysApi';
import { fcMore } from '@seeqdev/icons/seeq';
import { Icon, LoadingIndicator, TriggerWithDropdown } from '@seeqdev/qomponents';

export interface DisplayDropdownProps {
  item: any;
  isPinning: boolean;
  isPinned: boolean;
  togglePin: () => void;
  restrictExploration?: boolean;
}

type DropdownItems = ComponentProps<typeof TriggerWithDropdown>['dropdownItems'];

export const DisplayDropdown = ({
  item,
  isPinning,
  isPinned,
  togglePin,
  restrictExploration = false,
}: DisplayDropdownProps) => {
  enum MODAL {
    NONE = 'none',
    OVERWRITE = 'overwrite',
    RENAME = 'rename',
    MOVE = 'move',
    DELETE = 'delete',
  }

  const { t } = useTranslation();

  const [displayDropdown, setDisplayDropdown] = useState<DropdownItems>([]);
  const [isScaled, setIsScaled] = useState(false);
  const [displaysCount, setDisplaysCount] = useState(1);
  const [overwriteDisabled, setOverwriteDisabled] = useState(false);
  const [modalShown, setModalShown] = useState<MODAL>(MODAL.NONE);

  const displayLoadingId = useSearchDisplayLoadingId();
  const displayLoading = displayLoadingId === item.id;

  const withDisplayCountSet = (action: () => void) => {
    getDisplayCount(item)
      .then((displayCount) => {
        setDisplaysCount(displayCount);
        action();
      })
      .catch((e) => {
        errorToast({ httpResponseOrError: e });
      });
  };

  const generateDropdownEntry = ({
    translationKey,
    iconClass,
    inProgress = false,
    action = _.noop,
    disabled = false,
    tooltip,
  }: {
    translationKey: string;
    iconClass: string;
    inProgress?: boolean;
    action: () => void;
    doRender?: boolean;
    disabled?: boolean;
    tooltip?: string;
  }) => {
    return {
      key: `dropdown_wrapper_${translationKey}`,
      icon: iconClass,
      inProgress,
      onClick: (e?: Event) => {
        if (disabled) e?.stopPropagation();
        action();
      },
      labelTestId: `label_${translationKey}`,
      label: t(translationKey),
      itemCustomId: DISPLAY_DROPDOWN_ACTION_CUSTOM_ID,
      labelCustomId: DISPLAY_DROPDOWN_ACTION_CUSTOM_ID,
      iconTestId: `icon_${translationKey}`,
      iconCustomId: DISPLAY_DROPDOWN_ACTION_CUSTOM_ID,
      disabled,
      testId: `anchor_${translationKey}`,
      tooltip,
      tooltipDelay: 500,
      tooltipPlacement: 'right' as const,
    };
  };

  useEffect(() => {
    const actions = [
      {
        translationKey: 'SEARCH_DATA.DISPLAYS.OVERWRITE',
        iconClass: 'fc-overwrite',
        action: () => withDisplayCountSet(() => setModalShown(MODAL.OVERWRITE)),
        disabled: overwriteDisabled,
        tooltip: overwriteDisabled
          ? t('SEARCH_DATA.DISPLAYS.TOOLTIPS.OVERWRITE_DISABLED', {
              ITEM_PARENT_NAME: _.chain(item.ancestors).last().get('name').value(),
            })
          : undefined,
      },
      {
        translationKey: 'SEARCH_DATA.DISPLAYS.RENAME',
        iconClass: 'fc-rename',
        action: () => withDisplayCountSet(() => setModalShown(MODAL.RENAME)),
      },
      {
        translationKey: 'SEARCH_DATA.DISPLAYS.MOVE',
        iconClass: 'fa-arrows',
        action: () => setModalShown(MODAL.MOVE),
        disabled: isScaled,
        tooltip: isScaled ? 'SEARCH_DATA.DISPLAYS.TOOLTIPS.MOVE_DISABLED' : undefined,
      },
      {
        translationKey: isPinned ? 'SEARCH_DATA.DISPLAYS.UNPIN' : 'SEARCH_DATA.DISPLAYS.PIN',
        iconClass: isPinned ? 'fc-pin' : 'fc-pin-o',
        inProgress: isPinning,
        disabled: isPinning,
        action: () => !isPinning && togglePin(),
      },
      {
        translationKey: 'SEARCH_DATA.DISPLAYS.DELETE',
        iconClass: 'fc-trash',
        action: () => withDisplayCountSet(() => setModalShown(MODAL.DELETE)),
      },
    ];
    const actionsToRender = actions.map(generateDropdownEntry);
    setDisplayDropdown(actionsToRender);
  }, [overwriteDisabled, isScaled, isPinned, isPinning]);

  useEffect(() => {
    sqDisplaysApi
      .getDisplay({ id: item.id })
      .then(
        ({
          data: {
            template: { swapSourceAssetId },
          },
        }) => {
          const isScaledDisplay = !!swapSourceAssetId;
          setIsScaled(isScaledDisplay);
        },
      )
      .catch((error) => errorToast({ httpResponseOrError: error }));
  }, []);

  const trendStoreItems = useAllTrendStoreItems();
  useEffect(() => {
    const displayParentAssetId = _.last(
      item.ancestors as {
        id: string;
      }[],
    )?.id;
    const disabled = isScaled && !canOverwriteScaledDisplay(displayParentAssetId, trendStoreItems);
    setOverwriteDisabled(disabled);
  }, [item, isScaled, trendStoreItems]);

  return (
    <>
      <TriggerWithDropdown
        triggerIcon={
          displayLoading ? (
            <LoadingIndicator
              size="sm"
              color="currentColor"
              extraClassNames="searchBtn"
              testId="displayActionsIcon"
              customId={DISPLAY_DROPDOWN_ACTION_CUSTOM_ID}
            />
          ) : (
            <Icon
              icon={fcMore}
              type="inherit"
              extraClassNames="searchBtn"
              testId="displayActionsIcon"
              customId={DISPLAY_DROPDOWN_ACTION_CUSTOM_ID}
              tooltipDelay={500}
              tooltip={t('SEARCH_DATA.DISPLAYS.TOOLTIPS.SHOW_ACTIONS')}
              tooltipPlacement="top"
            />
          )
        }
        keepFocusInsideDropdown={false}
        setFocusOnTriggerOnClose={false}
        disabled={displayLoading}
        id={`display-actions-${item.id}`}
        dropdownItems={_.filter(displayDropdown, (action) => !!action)}
        placement="bottom"
      />

      {modalShown === MODAL.OVERWRITE && (
        <EventCapture>
          <OverwriteDisplayModal
            onClose={() => setModalShown(MODAL.NONE)}
            display={item}
            displaysCount={displaysCount}
          />
        </EventCapture>
      )}

      {modalShown === MODAL.RENAME && (
        <EventCapture>
          <RenameDisplayModal onClose={() => setModalShown(MODAL.NONE)} display={item} displaysCount={displaysCount} />
        </EventCapture>
      )}

      {modalShown === MODAL.MOVE && (
        <EventCapture>
          <MoveDisplayModal onClose={() => setModalShown(MODAL.NONE)} display={item} />
        </EventCapture>
      )}

      {modalShown === MODAL.DELETE && (
        <EventCapture>
          <DeleteDisplayModal
            isPinned={isPinned}
            onClose={() => setModalShown(MODAL.NONE)}
            display={item}
            displaysCount={displaysCount}
            restrictExploration={restrictExploration}
          />
        </EventCapture>
      )}
    </>
  );
};
